import {
  AdapterOptions,
  AnalyticsAdapter,
  AnalyticsDataPrefix,
} from '../../analyticsService.d';
import { analyticsUtils } from '../../analyticsUtils';

declare global {
  interface Window {
    amplitude?: any;
  }
}

export const amplitudeSdk =
  '!function(){"use strict";!function(e,t){var r=e.amplitude||{_q:[]};if(r.invoked){e.console&&console.error&&console.error("Amplitude snippet has been loaded.");}else{r.invoked=!0;var n=t.createElement("script");n.type="text/javascript",n.integrity="sha384-GS6YJWyepBi+TL3uXx5i7xx1UTA9iHaZr9q+5uNsuhzMb8c1SfkKW4Wee/IxZOW5",n.crossOrigin="anonymous",n.async=!0,n.src="https://cdn.amplitude.com/libs/analytics-browser-1.0.0-min.js.gz",n.onload=function(){e.amplitude.runQueuedFunctions||console.log("[Amplitude] Error: could not load SDK")};var s=t.getElementsByTagName("script")[0];function v(e,t){e.prototype[t]=function(){return this._q.push({name:t,args:Array.prototype.slice.call(arguments,0)}),this}}s.parentNode.insertBefore(n,s);for(var o=function(){return this._q=[],this},i=["add","append","clearAll","prepend","set","setOnce","unset","preInsert","postInsert","remove","getUserProperties"],a=0;a<i.length;a++)v(o,i[a]);r.Identify=o;for(var u=function(){return this._q=[],this},c=["getEventProperties","setProductId","setQuantity","setPrice","setRevenue","setRevenueType","setEventProperties"],p=0;p<c.length;p++)v(u,c[p]);r.Revenue=u;var d=["getDeviceId","setDeviceId","regenerateDeviceId","getSessionId","setSessionId","getUserId","setUserId","setOptOut","setTransport"],l=["init","add","remove","track","logEvent","identify","groupIdentify","setGroup","revenue"];function f(e){function t(t,r){e[t]=function(){var n={promise:new Promise((r=>{e._q.push({name:t,args:Array.prototype.slice.call(arguments,0),resolve:r})}))};if(r)return n}}for(var r=0;r<d.length;r++)t(d[r],!1);for(var n=0;n<l.length;n++)t(l[n],!0)}f(r),e.amplitude=r}}(window,document)}();';

export const amplitude = (
  instanceOptions?: AdapterOptions,
): AnalyticsAdapter => {
  const options = instanceOptions;

  function log(message: string): void {
    try {
      if (options?.debug) {
        options.logger(`AMP: ${message}`);
      }
    } catch (error) {
      console.log(`AMP: log error: ${error}`);
    }
  }

  function addVendor(): void {
    if (!verifyVendor()) {
      const head = window.document.getElementsByTagName('head')[0];
      const script = window.document.createElement('script');
      script.type = 'text/javascript';
      script.text = amplitudeSdk;
      head.appendChild(script);

      log(
        'addVendor - //cdn.amplitude.com/libs/analytics-browser-1.0.0-min.js.gz',
      );
    } else {
      log('addVendor - `amplitude` already added');
    }
  }

  function verifyVendor(): boolean {
    return typeof window !== 'undefined' && !!window.amplitude;
  }

  function initialise(id: string): boolean {
    if (!verifyVendor() || !id) {
      return false;
    }

    window.amplitude.init(id, null, {
      serverZone: 'EU',
    });

    log(`initialise: ${id}`);
    return true;
  }

  function grantConsent(): void {
    if (verifyVendor()) {
      window.amplitude.setOptOut(false);
    }
  }

  function revokeConsent(): void {
    if (verifyVendor()) {
      window.amplitude.setOptOut(true);
    }
  }

  function trackEvent(data: Record<string, unknown>): void {
    if (verifyVendor()) {
      const requiredData = analyticsUtils.getSpecificVendorData(
        AnalyticsDataPrefix.Amplitude,
        data,
      );

      const revenueProperties: any = requiredData?.revenueProperties;
      const userProperties = requiredData?.userProperties;
      const eventProperties = requiredData?.eventProperties;

      if (revenueProperties) {
        const event = new window.amplitude.Revenue()
          .setPrice(revenueProperties.price)
          .setEventProperties(eventProperties);

        window.amplitude.revenue(event);
        delete requiredData?.revenueProperties;
      }

      if (userProperties) {
        const identifyObj = new window.amplitude.Identify();
        Object.entries(userProperties).forEach(([key, value]) =>
          identifyObj.set(key, value),
        );
        window.amplitude.identify(identifyObj);
        delete requiredData?.userProperties;
      }

      const amplitudeData = analyticsUtils.dataConverter(requiredData);
      delete amplitudeData.label;

      const hasTrackingData = Object.keys(amplitudeData).length !== 0;
      if (!hasTrackingData) return;

      window.amplitude.track(requiredData.label, amplitudeData);

      log(`track - ${requiredData.label}`);
    }
  }

  return {
    addVendor,
    grantConsent,
    initialise,
    revokeConsent,
    trackEvent,
    verifyVendor,
  };
};
